import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface GuardrailDefinition {
  id: string;
  code: string;
  name: string;
  description: string | null;
  inputType: 'number' | 'select' | 'boolean';
  options: string[] | null;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
  isActive: boolean;
}

export function useGuardrailDefinitions(includeInactive = false) {
  return useQuery({
    queryKey: ['guardrail-definitions', { includeInactive }],
    queryFn: () =>
      api.get<{ definitions: GuardrailDefinition[] }>('/vcf/guardrail-definitions', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
  });
}

export function useCreateGuardrailDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Omit<GuardrailDefinition, 'id' | 'sortOrder' | 'isActive'> & {
        sortOrder?: number;
        isActive?: boolean;
      }
    ) => api.post('/vcf/guardrail-definitions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-definitions'] });
    },
  });
}

export function useUpdateGuardrailDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<GuardrailDefinition>) =>
      api.patch(`/vcf/guardrail-definitions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-definitions'] });
    },
  });
}

export function useDeleteGuardrailDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/guardrail-definitions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-definitions'] });
    },
  });
}
